import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
    Grid,
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip
} from '@mui/material';
import { loadVacationPeriods, saveVacationRequest } from './logic';
import './styles.css';
import { getUser, getCurrentAreaByEmployee } from '../../shared/auth-service';
import ErrorSnackbar from '../error-snackbar';
import BackDrop from '../../shared/backdropDE';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';

const VacationRequest = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

    const user = getUser();
    const companyId = user.countryCode.split('-')[0];
    const [area, setArea] = useState(null);
    const [params, setParams] = useState(null);
    const [openInstructions, setOpenInstructions] = useState(false);
    const [openErrorPopup, setOpenErrorPopup] = useState(false);
    const [errorPopupMessage, setErrorPopupMessage] = useState('');
    const [hasRequested, setHasRequested] = useState(false);
    const noPeriods = periods.length === 0;


    useEffect(() => {
      async function fetchData() {
        try {
          const area = await getCurrentAreaByEmployee(user.employeeId);
          setArea(area);
          const areaId = area.areaId;
    
          const params = {
            employeeId: user.employeeId,
            areaId,
            companyId
          };
          setParams(params);
    
          const data = await loadVacationPeriods(params);
          setPeriods(data);

          const hasRequested = data.some(p => p.onRequest);
          setHasRequested(hasRequested);

          if (!hasRequested && data.length > 0) 
            setOpenInstructions(true);
    
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    
      fetchData();
    }, []);
    

    const handleConfirm = async () => {
        if (!selectedPeriod) return;

        setIsSaveButtonDisabled(true);

        const payload = {
            AreaId: area.areaId,
            PeriodId: selectedPeriod.periodId,
            EmployeeId: user.employeeId,
            CampaignId: selectedPeriod.campaignId,
            CompanyId: parseInt(companyId),
            Active: true,
            CreationEmployee: user.username || user.employeeId,
            CreationDate: new Date().toISOString(),
            UpdateEmployee: null,
            Updated: null
        };

        try {
            const result = await saveVacationRequest(payload);
          
            if (result.data.status) 
              enqueueSnackbar(result.data.message, { variant: 'success' });
            else {
              setErrorPopupMessage(result.data.message || 'Ocurrió un error inesperado');
              setOpenErrorPopup(true);
            }

            
           
            const updatedPeriods = await loadVacationPeriods(params);
            setPeriods(updatedPeriods);

            setShowModal(false);
            
            const hasRequested = updatedPeriods.some(p => p.onRequest);
            setHasRequested(hasRequested);
  
            if (!hasRequested) 
              setOpenInstructions(true);

        } catch (error) {
            enqueueSnackbar("Error de red o servidor", { variant: 'error' });
        }finally {
          setIsSaveButtonDisabled(false);
        }
    };



    if (loading) return <BackDrop />;
    if (error) return <ErrorSnackbar message={error} />;

    return (
        <>
           <Grid container spacing={2} alignItems="center">
           <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ml: 1, mr: 1 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a3072' }}>
                  Solicitud de <span style={{ color: '#00b0ef' }}>Vacaciones</span>
                </Typography>
                <Tooltip title="Instrucciones" arrow>
                  <IconButton
                    color="info"
                    onClick={() => setOpenInstructions(true)}
                    sx={{ 
                      bgcolor: '#e3f2fd', 
                      '&:hover': { bgcolor: '#bbdefb' } ,
                      width: 48,
                      height: 48
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: 40 }} />
                  </IconButton>
                </Tooltip>

              </Box>
            </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: '#e3f2fd',
                p: 2,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                ml: 1,
                boxShadow: 1,
                maxWidth: 400,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#1a3072' }}>
                <strong>Nombre:</strong> {user.name} {user.lastName}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#1a3072' }}>
                <strong>Documento:</strong> {user.employeeId}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#1a3072' }}>
                <strong>Área:</strong> {area?.name || '...'}
              </Typography>
            </Box>
          </Grid>

          {noPeriods && (
            <Grid item xs={12}>
              <Box sx={{ m: 1 }}>
                <Alert
                  severity="info"
                  sx={{
                    width: '100%',
                    fontSize: { xs: '13px', sm: '14px' },
                    lineHeight: 1.6,
                    mt: 1,
                    mb: 2
                  }}
                >
                  <Typography sx={{ textAlign: 'justify' }}>
                    Actualmente <strong>no cuentas con un periodo pendiente por disfrutar.</strong> Para conocer la fecha en la que inicias tu próximo periodo, 
                    comunícate con el <strong>área de Nómina</strong>
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          )}

{/* 
          {hasRequested && (
            <Grid item xs={12}>
              <Box sx={{ m: 1 }}>
                <Alert
                  severity="warning"
                  sx={{
                    width: '100%',
                    fontSize: { xs: '13px', sm: '14px' },
                    lineHeight: 1.6,
                    mt: 1,
                    mb: 2
                  }}
                >
                  Tu solicitud debe ser aprobada para que tu período sea validado y podamos darte una respuesta.
                  Actualmente estamos aceptando aproximadamente el 90% de las solicitudes recibidas.
                </Alert>
              </Box>
            </Grid>
          )} */}



          <Grid item xs={12}>
            <div className="grid-periods">
              {periods.map((item, index) => {
                const isRequested = item.onRequest;

                return (
                  <div
                    key={index}
                    className={`period-box ${isRequested ? 'on-request' : ''}`}
                    onClick={() => {
                      if (!isRequested && item.slots > 0) {
                        setSelectedPeriod(item);
                        setShowModal(true);
                      }
                    }}
                  >
                    {isRequested ? (
                      <>
                        <div className="slot-section requested">
                          <span style={{ color: "#ffffff", fontSize: "24px", fontWeight: "bold" }}>✔</span>
                          <div style={{ color: "#ffffff", fontWeight: "bold" }}>Solicitado</div>
                        </div>
                        <div className="details-section bg-light-green">
                        <div><strong>Inicio:</strong> {item.startDate}</div>
                        <div><strong>Final:</strong> {item.endDate || 'Por definir'}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`slot-section ${item.slots > 0 ? 'available' : 'full'}`}>
                          <div className="slot-number">{item.slots}</div>
                          <div className="slot-label">Cupos</div>
                        </div>
                        <div className={`details-section ${item.slots > 0 ? 'bg-light-blue' : 'bg-light-red'}`}>
                          <div><strong>Inicio:</strong> {item.startDate}</div>
                          <div><strong>Final:</strong> {item.endDate || 'Por definir'}</div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </Grid>
        </Grid>

        <Dialog open={showModal} onClose={() => setShowModal(false)}>
          <DialogTitle>Confirmar solicitud</DialogTitle>
          <DialogContent>
            ¿Quieres confirmar las vacaciones del <strong>{selectedPeriod?.startDate}</strong> al <strong>{selectedPeriod?.endDate}</strong>?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button 
              onClick={handleConfirm} 
              variant="contained"
              disabled={isSaveButtonDisabled}
            >
            {isSaveButtonDisabled ? "Guardando..." : "Confirmar"}
            </Button>
          </DialogActions>
        </Dialog>


        {!hasRequested && !noPeriods && (
          <Dialog open={openInstructions} onClose={() => setOpenInstructions(false)}>
            <DialogContent>
              <Typography sx={{ textAlign: 'justify', mb: 1 }}>
                • Haz clic sobre el cuadro del <strong>período deseado para realizar la solicitud.</strong>
              </Typography>
              <Typography sx={{ textAlign: 'justify', mb: 1 }}>
                • Solo puedes seleccionar <strong>períodos con cupos disponibles.</strong>
              </Typography>
              <Typography sx={{ textAlign: 'justify' }}>
                • Una vez confirmes, tu solicitud quedara <strong>registrada y marcada como solicitada.</strong>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenInstructions(false)}>
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <Dialog open={openErrorPopup} onClose={() => setOpenErrorPopup(false)}>
          <DialogTitle>
            ⚠️ Error en la solicitud
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">{errorPopupMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenErrorPopup(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

      </>
    );
};

export default VacationRequest;
